import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactDetailPanel = ({ contact, onClose, onSave, onCall }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState(contact || {});

  if (!contact) {
    return (
      <div className="w-96 bg-white border-l border-slate-200 p-6 flex items-center justify-center">
        <div className="text-center text-slate-500">
          <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-sm">Detayları görüntülemek için bir kişi seçin</p>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    onSave(editedContact);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedContact(contact);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Yüksek': return 'bg-red-100 text-red-800 border-red-200';
      case 'Orta': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Düşük': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  return (
    <div className="w-96 bg-white border-l border-slate-200 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Kişi Detayları</h2>
          <div className="flex items-center space-x-2">
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                iconName="Edit"
              >
                Düzenle
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
            />
          </div>
        </div>

        {/* Avatar and Basic Info */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
            <Icon name="User" size={24} color="var(--color-text-secondary)" />
          </div>
          <div>
            {isEditing ? (
              <Input
                type="text"
                value={editedContact?.name || ''}
                onChange={(e) => setEditedContact({...editedContact, name: e?.target?.value})}
                className="mb-2"
              />
            ) : (
              <h3 className="text-xl font-semibold text-slate-900">{contact?.name}</h3>
            )}
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(contact?.priority)}`}>
                {contact?.priority}
              </span>
              <div className="flex items-center text-sm text-slate-600">
                <div className={`w-2 h-2 rounded-full mr-1 ${
                  contact?.status === 'Aktif' ? 'bg-success' :
                  contact?.status === 'Beklemede' ? 'bg-warning' : 'bg-slate-400'
                }`} />
                {contact?.status}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onCall(contact)}
            iconName="Phone"
            className="flex-1"
          >
            Ara
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="MessageSquare"
            className="flex-1"
          >
            Mesaj
          </Button>
        </div>
      </div>
      {/* Contact Information */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* İletişim Bilgileri */}
          <div>
            <h4 className="text-sm font-medium text-slate-900 mb-3">İletişim Bilgileri</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={16} className="text-slate-500" />
                {isEditing ? (
                  <Input
                    type="tel"
                    value={editedContact?.phone || ''}
                    onChange={(e) => setEditedContact({...editedContact, phone: e?.target?.value})}
                    placeholder="Telefon numarası"
                  />
                ) : (
                  <span className="text-sm text-slate-900">{contact?.phone}</span>
                )}
              </div>
              
              {contact?.alternativePhone && (
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={16} className="text-slate-500" />
                  {isEditing ? (
                    <Input
                      type="tel"
                      value={editedContact?.alternativePhone || ''}
                      onChange={(e) => setEditedContact({...editedContact, alternativePhone: e?.target?.value})}
                      placeholder="Alternatif telefon"
                    />
                  ) : (
                    <span className="text-sm text-slate-900">{contact?.alternativePhone}</span>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={16} className="text-slate-500" />
                {isEditing ? (
                  <Input
                    type="email"
                    value={editedContact?.email || ''}
                    onChange={(e) => setEditedContact({...editedContact, email: e?.target?.value})}
                    placeholder="E-posta adresi"
                  />
                ) : (
                  <span className="text-sm text-slate-900">{contact?.email}</span>
                )}
              </div>
            </div>
          </div>

          {/* Şirket Bilgileri */}
          <div>
            <h4 className="text-sm font-medium text-slate-900 mb-3">Şirket Bilgileri</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Building" size={16} className="text-slate-500" />
                {isEditing ? (
                  <Input
                    type="text"
                    value={editedContact?.company || ''}
                    onChange={(e) => setEditedContact({...editedContact, company: e?.target?.value})}
                    placeholder="Şirket adı"
                  />
                ) : (
                  <span className="text-sm text-slate-900">{contact?.company}</span>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-slate-500" />
                {isEditing ? (
                  <Input
                    type="text"
                    value={editedContact?.location || ''}
                    onChange={(e) => setEditedContact({...editedContact, location: e?.target?.value})}
                    placeholder="Konum"
                  />
                ) : (
                  <span className="text-sm text-slate-900">{contact?.location}</span>
                )}
              </div>
            </div>
          </div>

          {/* İletişim Geçmişi */}
          <div>
            <h4 className="text-sm font-medium text-slate-900 mb-3">Son İletişimler</h4>
            <div className="space-y-2">
              {contact?.recentInteractions?.map((interaction, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                  <Icon 
                    name={interaction?.type === 'call' ? 'Phone' : 'MessageSquare'} 
                    size={14} 
                    className="text-slate-500 mt-0.5" 
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-900">
                        {interaction?.type === 'call' ? 'Telefon Görüşmesi' : 'Mesaj'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatDate(interaction?.date)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">{interaction?.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notlar */}
          <div>
            <h4 className="text-sm font-medium text-slate-900 mb-3">Notlar</h4>
            {isEditing ? (
              <textarea
                className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none"
                rows={4}
                value={editedContact?.notes || ''}
                onChange={(e) => setEditedContact({...editedContact, notes: e?.target?.value})}
                placeholder="Kişi hakkında notlar..."
              />
            ) : (
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                {contact?.notes || 'Henüz not eklenmemiş.'}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Edit Actions */}
      {isEditing && (
        <div className="p-6 border-t border-slate-200">
          <div className="flex space-x-3">
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              iconName="Check"
              className="flex-1"
            >
              Kaydet
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              iconName="X"
              className="flex-1"
            >
              İptal
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetailPanel;